import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  User, ChevronLeft, Trophy, Flame, Award, Calendar,
  UserCheck, UserX, TrendingUp, History, RefreshCw, Loader2,
  Droplets, Beef, CandyOff, Dumbbell, Footprints, Moon, Target, Zap,
  Check, X, ChevronDown, ChevronUp, Star, Clock
} from 'lucide-react';

// Icon mapping for actions
const iconMap = {
  droplets: Droplets,
  beef: Beef,
  'candy-off': CandyOff,
  dumbbell: Dumbbell,
  footprints: Footprints,
  moon: Moon,
  target: Target,
  zap: Zap,
  star: Star,
  trophy: Trophy,
  flame: Flame
};

const getIcon = (iconName, size = 16) => {
  const IconComponent = iconMap[iconName] || Target;
  return <IconComponent size={size} />;
};

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [expandedCheckins, setExpandedCheckins] = useState({});
  const [showAllCheckins, setShowAllCheckins] = useState(false);

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      const res = await apiClient.get(`/admin/users/${userId}`);
      setUserData(res.data);
    } catch (err) {
      console.error('Failed to fetch user details:', err);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async () => {
    const newStatus = userData.user.is_active === false ? true : false;
    try {
      await apiClient.put(`/admin/users/${userId}`, { is_active: newStatus });
      toast.success(newStatus ? 'User activated' : 'User deactivated');
      fetchUserDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user status');
    }
  };

  const recalculateBadges = async () => {
    if (!window.confirm('This will recalculate badges based on actual check-in history. Incorrect badges will be removed and missing badges will be awarded. Continue?')) {
      return;
    }
    
    setRecalculating(true);
    try {
      const res = await apiClient.post(`/admin/users/${userId}/recalculate-badges`);
      const data = res.data;
      
      if (data.badges_added.length > 0 || data.badges_removed.length > 0) {
        toast.success(
          `Badges recalculated: ${data.badges_added.length} added, ${data.badges_removed.length} removed`,
          { duration: 5000 }
        );
        if (data.badges_removed.length > 0) {
          toast.info(`Removed: ${data.badges_removed.join(', ')}`, { duration: 5000 });
        }
        if (data.badges_added.length > 0) {
          toast.info(`Added: ${data.badges_added.join(', ')}`, { duration: 5000 });
        }
      } else {
        toast.success('Badges verified - no changes needed');
      }
      
      // Refresh user data
      fetchUserDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to recalculate badges');
    } finally {
      setRecalculating(false);
    }
  };

  const toggleCheckinExpand = (checkinId) => {
    setExpandedCheckins(prev => ({
      ...prev,
      [checkinId]: !prev[checkinId]
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!userData) {
    return (
      <Layout>
        <div className="p-4 md:p-8 text-center">
          <p className="text-stone-600">User not found</p>
          <Button onClick={() => navigate('/admin/users')} className="mt-4">
            Back to Users
          </Button>
        </div>
      </Layout>
    );
  }

  const { user, recent_checkins, monthly_breakdown, action_streaks, all_actions } = userData;
  const displayedCheckins = showAllCheckins ? recent_checkins : recent_checkins?.slice(0, 14);

  // Create a map of all actions for the checkin display
  const actionMap = {};
  all_actions?.forEach(a => { actionMap[a.id] = a; });

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/users')}
          className="mb-6 text-stone-600 hover:text-stone-900"
        >
          <ChevronLeft size={20} />
          Back to Users
        </Button>

        {/* User Header */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                user.is_active === false ? 'bg-red-500/20' : 'bg-[#EFB342]/20'
              }`}>
                <User size={32} className={user.is_active === false ? 'text-red-500' : 'text-[#EFB342]'} />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">{user.name}</h1>
                <p className="text-stone-600">{user.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  {user.is_active === false ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">Inactive</span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Active</span>
                  )}
                  <span className="text-xs text-stone-500">
                    Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  {user.timezone && (
                    <span className="text-xs text-stone-500">• {user.timezone}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={recalculateBadges}
                disabled={recalculating}
                className="bg-purple-600 hover:bg-purple-700 text-stone-900"
                data-testid="recalculate-badges-btn"
              >
                {recalculating ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Recalculating...</>
                ) : (
                  <><RefreshCw size={18} className="mr-2" /> Recalculate Badges</>
                )}
              </Button>
              <Button
                onClick={toggleUserStatus}
                className={user.is_active === false 
                  ? 'bg-green-500 hover:bg-green-600 text-stone-900'
                  : 'bg-red-500 hover:bg-red-600 text-stone-900'
                }
              >
                {user.is_active === false ? (
                  <><UserCheck size={18} className="mr-2" /> Activate User</>
                ) : (
                  <><UserX size={18} className="mr-2" /> Deactivate User</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Trophy className="text-[#EFB342]" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{user.total_points || 0}</p>
                <p className="text-xs text-stone-500">Total Points</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Star className="text-blue-400" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{user.monthly_points || 0}</p>
                <p className="text-xs text-stone-500">This Month</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Flame className="text-orange-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{user.current_streak || 0}</p>
                <p className="text-xs text-stone-500">Current Streak</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="text-green-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{user.longest_streak || 0}</p>
                <p className="text-xs text-stone-500">Longest Streak</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Award className="text-purple-500" size={24} />
              <div>
                <p className="text-2xl font-bold text-stone-900">{user.badges?.length || 0}</p>
                <p className="text-xs text-stone-500">Badges</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Streaks - Current Status */}
        {action_streaks && action_streaks.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
            <h2 className="font-heading text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Flame className="text-orange-500" size={20} />
              Action Streaks (Current)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {action_streaks.map((streak) => (
                <div 
                  key={streak.action_id} 
                  className={`p-3 rounded-lg ${
                    streak.streak >= 7 ? 'bg-[#EFB342]/20 border border-[#EFB342]/30' : 'bg-stone-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[#EFB342]">{getIcon(streak.action_icon)}</span>
                    <span className="text-stone-900 font-medium text-sm truncate">{streak.action_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-lg font-bold ${streak.streak >= 7 ? 'text-[#EFB342]' : 'text-stone-600'}`}>
                      {streak.streak} days
                    </span>
                    {streak.last_date && (
                      <span className="text-xs text-stone-500">
                        {new Date(streak.last_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Check-in History */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-stone-900 flex items-center gap-2">
              <Calendar className="text-[#EFB342]" size={20} />
              Check-in History ({recent_checkins?.length || 0} days)
            </h2>
            {recent_checkins?.length > 14 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAllCheckins(!showAllCheckins)}
                className="border-stone-300 text-stone-700"
              >
                {showAllCheckins ? 'Show Less' : `Show All (${recent_checkins.length})`}
              </Button>
            )}
          </div>
          
          {displayedCheckins && displayedCheckins.length > 0 ? (
            <div className="space-y-2">
              {displayedCheckins.map((checkin) => {
                const isExpanded = expandedCheckins[checkin.id];
                const actionCount = checkin.action_ids?.length || 0;
                const isFullSend = actionCount >= 10;
                const checkinDate = new Date(checkin.date + 'T12:00:00');
                
                return (
                  <div 
                    key={checkin.id} 
                    className={`rounded-lg border ${
                      isFullSend 
                        ? 'bg-[#EFB342]/10 border-[#EFB342]/30' 
                        : 'bg-stone-50 border-stone-300/50'
                    }`}
                  >
                    {/* Checkin Header - Always visible */}
                    <button
                      onClick={() => toggleCheckinExpand(checkin.id)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-[#F0EBE1] transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-[50px]">
                          <p className="text-lg font-bold text-stone-900">
                            {checkinDate.getDate()}
                          </p>
                          <p className="text-xs text-stone-500">
                            {checkinDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isFullSend && (
                            <span className="px-2 py-0.5 bg-[#EFB342] text-black text-xs rounded-full font-medium">
                              FULL SEND
                            </span>
                          )}
                          <span className="text-stone-600 text-sm">{actionCount} actions</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#EFB342] text-lg">{checkin.total_points} pts</span>
                        {isExpanded ? <ChevronUp size={18} className="text-stone-600" /> : <ChevronDown size={18} className="text-stone-600" />}
                      </div>
                    </button>
                    
                    {/* Expanded Actions Detail */}
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-stone-300/50 mt-1 pt-3">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          {all_actions?.map(action => {
                            const isChecked = checkin.action_ids?.includes(action.id);
                            return (
                              <div 
                                key={action.id}
                                className={`flex items-center gap-2 p-2 rounded-lg ${
                                  isChecked ? 'bg-green-500/20' : 'bg-[#F0EBE1]'
                                }`}
                              >
                                {isChecked ? (
                                  <Check size={14} className="text-green-500 flex-shrink-0" />
                                ) : (
                                  <X size={14} className="text-stone-400 flex-shrink-0" />
                                )}
                                <span className={`text-xs truncate ${isChecked ? 'text-green-400' : 'text-stone-500'}`}>
                                  {action.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {checkin.created_at && (
                          <p className="text-xs text-stone-500 mt-2 flex items-center gap-1">
                            <Clock size={12} />
                            Submitted: {new Date(checkin.created_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-stone-500 text-center py-8">No check-ins yet</p>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Badges Earned */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6">
            <h2 className="font-heading text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
              <Award className="text-[#EFB342]" size={20} />
              Badges Earned ({user.badges?.length || 0})
            </h2>
            {user.badges && user.badges.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {[...user.badges]
                  .sort((a, b) => (b.earned_at || '').localeCompare(a.earned_at || ''))
                  .map((badge, i) => {
                    const earnedDate = badge.earned_at 
                      ? new Date(badge.earned_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })
                      : 'Unknown';
                    
                    // Check if earned this month
                    const now = new Date();
                    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
                    const isThisMonth = (badge.earned_at || '') >= currentMonthStart;
                    
                    return (
                      <div 
                        key={i} 
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isThisMonth ? 'bg-[#EFB342]/10 border border-[#EFB342]/30' : 'bg-stone-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#EFB342] rounded-lg flex items-center justify-center">
                            <Award className="text-black" size={20} />
                          </div>
                          <div>
                            <p className="text-stone-900 font-medium">{badge.badge_name}</p>
                            <p className="text-xs text-stone-500">Earned {earnedDate}</p>
                            {badge.for_date && badge.for_date !== badge.earned_at?.slice(0, 10) && (
                              <p className="text-xs text-stone-600">For: {badge.for_date}</p>
                            )}
                          </div>
                        </div>
                        {isThisMonth && (
                          <span className="px-2 py-1 bg-[#EFB342]/20 text-[#EFB342] text-xs rounded-full font-medium">
                            This Month
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No badges yet</p>
            )}
          </div>

          {/* Monthly Breakdown */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6">
            <h2 className="font-heading text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
              <History className="text-[#EFB342]" size={20} />
              Monthly Points History
            </h2>
            {monthly_breakdown && monthly_breakdown.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {monthly_breakdown.map((month) => {
                  const [year, monthNum] = month._id.split('-');
                  const monthName = new Date(year, monthNum - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                  return (
                    <div key={month._id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <span className="text-stone-700">{monthName}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-stone-500">{month.check_in_days} days</span>
                        <span className="font-bold text-[#EFB342]">{month.total_points} pts</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No activity history</p>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminUserDetail;
